import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useLocalStorage } from 'usehooks-ts'

import ChatBubble from './chat-bubble'
import { useSearch } from '@/components/search-provider'

import ArrowIcon from '@/icons/bwi/arrow-right.svg'

import { isAlgoliaAiConfigValid } from './api'
import { useAskAiChat } from './use-ask-ai-chat'
import { useTranslations } from '@/utils/locale'
import { cx } from '@/utils/strings'

type AiSearchFieldProps = {
  className?: string
  size?: 'sm' | 'md'
  inputLocation?: 'top' | 'bottom'
  defaultValue?: string
  agentId?: string
  trackingMetadata?: Record<string, any>
}

export default function AiSearchField({
  className,
  size = 'md',
  inputLocation = 'bottom',
  defaultValue = '',
  agentId,
  trackingMetadata,
}: AiSearchFieldProps) {
  if (!isAlgoliaAiConfigValid()) return null

  const { t } = useTranslations()
  const { isAiLoading: isLoading, setIsAiLoading: setIsLoading, isChatOpen, aiQuery, setAiQuery } = useSearch()
  const {
    messages,
    status,
    isLoading: isSdkLoading,
    error,
    transportError,
    sendPrompt,
    stop,
    handleFeedback,
  } = useAskAiChat(agentId, trackingMetadata)

  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (isChatOpen) {
      inputRef.current?.focus()
    }
  }, [isChatOpen])

  const [activeSources, setActiveSources] = useState<{
    messageId: string
  } | null>(null)

  const [askAiQuery, , removeAskAiQuery] = useLocalStorage<string | undefined>('askAiQuery', undefined)
  useEffect(() => {
    if (askAiQuery) {
      handleSendMessage(askAiQuery).then(() => {
        removeAskAiQuery() // Clear after message is processed
      })
    }
  }, [askAiQuery])

  useEffect(() => {
    if (aiQuery) {
      handleSendMessage(aiQuery).then(() => {
        setAiQuery('') // Clear after message is processed
      })
    }
  }, [aiQuery])

  useEffect(() => {
    setIsLoading(isSdkLoading)
    if (!isSdkLoading) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 10)
    }
  }, [isSdkLoading, setIsLoading])

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const shouldAutoScroll = useRef(true)
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      const { scrollHeight, clientHeight } = scrollContainerRef.current
      scrollContainerRef.current.scrollTop = scrollHeight - clientHeight
    }
  }

  // Auto-scroll when messages update or during streaming
  useEffect(() => {
    if (shouldAutoScroll.current && (status === 'streaming' || status === 'submitted')) {
      scrollToBottom()
    }
  }, [messages, status])

  const handleSendMessage = async (message: string) => {
    shouldAutoScroll.current = true
    await sendPrompt(message)
  }

  const handleSubmit = () => {
    const search = inputRef.current?.value || ''
    if (!search?.trim()) return
    handleSendMessage(search)
    if (inputRef.current) {
      inputRef.current.value = ''
      // Reset textarea height after submit
      inputRef.current.style.height = 'auto'
    }
  }

  return (
    <div
      className={cx(
        'flex bg-secondary-50/40 dark:bg-secondary-800 relative overflow-hidden w-full h-full',
        size === 'md' && 'rounded-2xl p-2 md:p-3',
        size === 'sm' && 'rounded-xl p-2',
        inputLocation === 'top' ? 'flex-col-reverse' : 'flex-col',
        className
      )}
    >
      <div
        className={cx(
          'absolute top-0 left-0 z-[2] w-full h-full transition-opacity opacity-0 bg-black',
          activeSources ? 'opacity-30' : 'pointer-events-none'
        )}
        onClick={() => {
          setActiveSources(null)
        }}
      />
      {messages.length >= 1 && (
        <motion.div
          className={cx(
            'relative flex h-full grow shrink min-h-0 overflow-x-clip overflow-y-auto scrollbar-thumb-secondary-500'
          )}
          ref={scrollContainerRef}
          onScroll={() => {
            if (scrollContainerRef.current) {
              const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
              const atBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px threshold

              // Enable auto-scroll if user scrolls to bottom, disable if they scroll away
              shouldAutoScroll.current = atBottom
            }
          }}
        >
          <div
            className={cx('flex flex-col w-full h-full', size === 'md' && 'gap-8 p-4', size === 'sm' && 'gap-4 p-2')}
          >
            {messages.length > 0 && (
              <div
                className={cx(
                  'flex flex-col flex-shrink',
                  size === 'md' && 'gap-6 py-2 md:py-8 md:px-2',
                  size === 'sm' && 'gap-4 py-4'
                )}
              >
                {messages.map((item) => {
                  const messageId = item.id
                  return (
                    <div key={messageId} className={cx(item.role === 'user' && 'text-right')}>
                      {item.role === 'user' ? (
                        <ChatBubble {...item} state="complete" size={size} animated={false} />
                      ) : (
                        <ChatBubble
                          {...item}
                          state={
                            status === 'streaming' && item.id === messages[messages.length - 1].id
                              ? 'streaming'
                              : 'complete'
                          }
                          size={size}
                          animated={false}
                          isSelected={!!messageId && activeSources?.messageId === messageId}
                          onFeedback={(feedback) => handleFeedback(messageId, feedback)}
                          onStreamUpdate={() => {
                            if (shouldAutoScroll.current) {
                              scrollToBottom()
                            }
                          }}
                        />
                      )}
                    </div>
                  )
                })}
                {(transportError || error) && (
                  <div className="text-left">
                    <ChatBubble
                      id={`error-${messages.length}`} // Unique ID for error message
                      role="assistant"
                      state="error"
                      parts={[
                        { type: 'text', text: transportError || error?.message || 'An unexpected error occurred.' },
                      ]}
                      animated={false}
                      size={size}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
      {messages.length === 0 && inputLocation === 'top' && <div className="flex-grow" />}
      <form
        className="w-full mx-auto"
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <div className={cx('relative', isLoading && 'opacity-80')}>
          <div className="flex">
            <textarea
              ref={inputRef}
              name="search"
              placeholder={t('Ask anything...')}
              className={cx(
                'w-full border-2 resize-none h-full overflow-hidden',
                'focus:outline-none focus:ring-0',
                size === 'md' && 'rounded-lg lg:placeholder:text-xl lg:text-xl pl-6 pr-32 pt-6 pb-[23px] lg:py-5',
                size === 'sm' && 'rounded-md text-base placeholder:text-base pl-4 pr-24 py-3',
                !isLoading &&
                  'bg-white dark:bg-secondary-800 dark:text-white placeholder:text-black dark:placeholder:text-secondary-200 border-secondary-100 dark:border-secondary-600/50 focus:border-primaryBlue/60 dark:focus:border-primaryBlue/60',
                isLoading &&
                  'border-secondary-100 dark:border-secondary-700 dark:bg-secondary-800 dark:text-secondary-400 placeholder:text-secondary-400'
              )}
              autoComplete="off"
              disabled={isLoading || status === 'submitted' || status === 'streaming'}
              autoFocus
              rows={1}
              defaultValue={defaultValue}
              onInput={(e) => {
                // Auto-resize functionality
                const target = e.target as HTMLTextAreaElement
                target.style.height = 'auto'
                target.style.height = `${Math.min(target.scrollHeight, 460)}px` // 160px = max-h-40
              }}
              onKeyDown={(e) => {
                // Submit on Enter (without Shift)
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit()
                }
                if (e.key === 'Escape' && (status === 'submitted' || status === 'streaming')) {
                  e.preventDefault()
                  stop()
                }
              }}
            />
          </div>

          {!isLoading ? (
            <button
              type="submit"
              className={cx(
                'absolute flex items-center justify-center rounded-full aspect-square',
                'transition-colors bg-primaryBlue dark:bg-white hover:bg-bw-teal-light-highlight dark:text-black hover:text-black text-white',
                'bottom-0 top-0 my-auto',
                size === 'md' && 'size-9 right-4',
                size === 'sm' && 'size-8 right-3'
              )}
              aria-label={t('Send message')}
              title={t('Send message')}
            >
              <ArrowIcon
                className={cx('mx-auto translate-x-px', size === 'md' && 'size-6', size === 'sm' && 'size-5.5')}
              />
            </button>
          ) : (
            <button
              type="button"
              className={cx(
                'absolute flex items-center justify-center rounded-full aspect-square',
                'transition-colors bg-secondary-200 hover:bg-accent-melon',
                'bottom-0 top-0 my-auto',
                size === 'md' && 'size-9 right-4',
                size === 'sm' && 'size-8 right-3'
              )}
              title={t('Stop')}
              onClick={stop}
            >
              <div className="size-1/3 bg-white/80" />
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
